// v2.1
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Upload, FileText, Image as ImageIcon, Video, Zap, Check, Info, Coins, Camera, RefreshCw, X, Presentation, BookOpen, FileImage } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Toaster, toast } from 'sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── AUTH HELPERS ─────────────────────────────────────────────────────────────

const getDeviceId = () => {
  let id = localStorage.getItem('deviceId');
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('deviceId', id);
  }
  return id;
};

const getSessionToken = () => localStorage.getItem('sessionToken');
const setSessionToken = (token) => localStorage.setItem('sessionToken', token);
const clearSession = () => {
  localStorage.removeItem('sessionToken');
  localStorage.removeItem('authedEmail');
};
const getAuthedEmail = () => localStorage.getItem('authedEmail');
const setAuthedEmail = (email) => localStorage.setItem('authedEmail', email);
const isLoggedIn = () => !!getSessionToken();

const axiosConfig = () => ({
  headers: { 'X-Session-Token': getSessionToken() }
});

// ─── ERROR BOUNDARY ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Authentico crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white flex items-center justify-center px-6">
          <div className="text-center max-w-sm">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4 text-2xl">⚠️</div>
            <h2 className="font-bold text-lg mb-2">Something went wrong</h2>
            <p className="text-muted-foreground text-sm mb-6">Authentico hit an unexpected error. Tap below to reload. If this keeps happening, contact <a href="mailto:support@authenticoapp.com" className="text-indigo-600 underline">support@authenticoapp.com</a></p>
            <button
              onClick={() => window.location.reload()}
              className="bg-indigo-600 text-white rounded-full px-6 py-2.5 text-sm font-semibold active:scale-95 transition-transform"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


// ─── AUTH PAGE ────────────────────────────────────────────────────────────────
const AuthPage = ({ onAuthSuccess }) => {
  const features = [
    { emoji: '📝', label: 'Text Detection', desc: 'Paste any text' },
    { emoji: '🖼️', label: 'Image Scanning', desc: 'AI-generated images' },
    { emoji: '📄', label: 'Documents', desc: 'PDF, DOCX, PPTX' },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toaster />

      {/* ── LEFT PANEL desktop ── */}
      <div className="hidden md:flex md:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #6366f1 0%, #4f46e5 60%, #3730a3 100%)' }}>
        <div className="absolute top-[-80px] right-[-80px] w-72 h-72 rounded-full opacity-20" style={{ background: '#818cf8' }} />
        <div className="absolute bottom-[-60px] left-[-60px] w-64 h-64 rounded-full opacity-15" style={{ background: '#a5b4fc' }} />
        <div className="flex items-center gap-3 relative z-10">
          <img src="/logo.png" alt="authentico" className="w-10 h-10 rounded-2xl shadow-lg" />
          <span className="text-white text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>authentico</span>
        </div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/90 text-xs font-semibold tracking-wide uppercase">AI Detection</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Start detecting<br />AI content<br />right now.
          </h1>
          <p className="text-indigo-200 text-base leading-relaxed max-w-xs">For teachers, researchers, and anyone who needs to know if content was made by AI.</p>
          <div className="flex flex-col gap-3 mt-8">
            {features.map(f => (
              <div key={f.label} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-4 py-3">
                <span className="text-xl">{f.emoji}</span>
                <div>
                  <p className="text-white text-sm font-semibold">{f.label}</p>
                  <p className="text-indigo-300 text-xs">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10"><p className="text-indigo-300 text-xs">SCAN. DETECT. TRUST.</p></div>
      </div>

      {/* ── RIGHT PANEL / full screen mobile ── */}
      <div className="flex-1 flex flex-col justify-center items-center md:px-6 md:py-0 bg-white">
        {/* Mobile indigo header */}
        <div className="md:hidden w-full px-6 pt-10 pb-8 relative overflow-hidden"
          style={{ background: 'linear-gradient(145deg, #6366f1 0%, #4f46e5 60%, #3730a3 100%)' }}>
          <div className="absolute top-[-40px] right-[-40px] w-40 h-40 rounded-full opacity-20" style={{ background: '#818cf8' }} />
          <div className="flex items-center gap-2.5 mb-5 relative z-10">
            <img src="/logo.png" alt="authentico" className="w-9 h-9 rounded-2xl shadow-lg" />
            <span className="text-white text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>authentico</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 mb-3 relative z-10">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-white/90 text-xs font-semibold tracking-wide uppercase">AI Detection</span>
          </div>
          <h1 className="text-2xl font-extrabold text-white leading-tight mb-1.5 relative z-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Start detecting AI<br />content right now.
          </h1>
          <p className="text-indigo-200 text-sm leading-relaxed relative z-10">Text, images, documents — know what's real.</p>
          <div className="flex gap-2 mt-4 flex-wrap relative z-10">
            {features.map(f => (
              <span key={f.label} className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white rounded-full px-3 py-1 text-xs font-semibold">
                {f.emoji} {f.label}
              </span>
            ))}
          </div>
        </div>

        <div className="w-full max-w-sm px-6 py-8 md:py-0">
          <div className="hidden md:block mb-5">
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Create your account</h2>
            <p className="text-sm text-gray-400 mt-0.5">Free to start — no credit card needed.</p>
          </div>
          <AuthForm onSuccess={onAuthSuccess} />
        </div>
      </div>
    </div>
  );
};

// ─── AUTH MODAL ───────────────────────────────────────────────────────────────
// Global context so any component can trigger the auth modal
const AuthModalContext = React.createContext({ open: () => {} });
const useAuthModal = () => React.useContext(AuthModalContext);

const AuthModalProvider = ({ children }) => {
  const [show, setShow] = useState(false);
  const [onSuccessCb, setOnSuccessCb] = useState(null);

  const open = (cb) => {
    setOnSuccessCb(() => cb || null);
    setShow(true);
  };
  const close = () => setShow(false);

  const handleSuccess = (email) => {
    setShow(false);
    if (onSuccessCb) onSuccessCb(email);
  };

  return (
    <AuthModalContext.Provider value={{ open }}>
      {children}
      {show && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} />
          {/* Sheet — slides up on mobile, centered card on desktop */}
          <div className="relative w-full md:max-w-md bg-white md:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
            {/* Close button */}
            <button onClick={close} className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500 text-sm font-bold">✕</button>

            {/* Indigo top strip */}
            <div className="px-6 pt-6 pb-5 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
              <div className="absolute top-[-30px] right-[-30px] w-28 h-28 rounded-full opacity-20" style={{ background: '#818cf8' }} />
              <div className="flex items-center gap-2.5 relative z-10">
                <img src="/logo.png" alt="authentico" className="w-8 h-8 rounded-xl shadow" />
                <span className="text-white font-bold text-base tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>authentico</span>
              </div>
              <p className="text-white font-extrabold text-lg mt-3 relative z-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Create a free account<br />to start scanning.
              </p>
              <p className="text-indigo-200 text-sm mt-1 relative z-10">No credit card needed.</p>
            </div>

            {/* Form */}
            <AuthForm onSuccess={handleSuccess} />
          </div>
        </div>
      )}
    </AuthModalContext.Provider>
  );
};

// Shared form used by both the full AuthPage and the modal
const AuthForm = ({ onSuccess, compact = false }) => {
  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleBtnRef = useRef(null);

  const handleGoogleCredential = async (response) => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/google`, {
        credential: response.credential,
        device_id: getDeviceId(),
      });
      setSessionToken(res.data.token);
      setAuthedEmail(res.data.email);
      onSuccess(res.data.email);
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    let cancelled = false;
    let attempts = 0;

    const tryRender = () => {
      if (cancelled) return;
      if (window.google?.accounts?.id && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredential,
        });
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          width: 320,
          text: 'continue_with',
        });
      } else if (attempts < 20) {
        attempts += 1;
        setTimeout(tryRender, 250);
      }
    };
    tryRender();

    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async () => {
    setError('');
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    if (mode === 'signup' && password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (mode === 'signup' && password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      const endpoint = mode === 'signup' ? '/auth/signup' : '/auth/login';
      const res = await axios.post(`${API}${endpoint}`, {
        email: email.trim().toLowerCase(),
        password,
        device_id: getDeviceId(),
      });
      setSessionToken(res.data.token);
      setAuthedEmail(res.data.email);
      onSuccess(res.data.email);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 py-5">
      {/* Tab switcher */}
      <div className="flex bg-gray-100 rounded-2xl p-1 mb-4">
        {['signup', 'login'].map(m => (
          <button key={m}
            onClick={() => { setMode(m); setError(''); setPassword(''); setConfirmPassword(''); }}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
            {m === 'signup' ? 'Get Started' : 'Sign In'}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 bg-red-50 border border-red-100 text-red-600 rounded-2xl px-3 py-2.5 text-sm flex gap-2 items-start">
          <span>⚠️</span><span>{error}</span>
        </div>
      )}

      {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
        <>
          <div className="flex justify-center mb-3" ref={googleBtnRef} />
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px bg-gray-200 flex-1" />
            <span className="text-xs text-gray-400 font-medium">or</span>
            <div className="h-px bg-gray-200 flex-1" />
          </div>
        </>
      )}

      <div className="space-y-2.5">
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Email address"
          className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50 transition" />
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Password (min. 6 characters)"
          className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50 transition" />
        {mode === 'signup' && (
          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            placeholder="Confirm password"
            className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50 transition" />
        )}
      </div>

      <button onClick={handleSubmit} disabled={loading}
        className="w-full mt-4 py-3.5 rounded-2xl font-bold text-sm text-white transition-all active:scale-95 disabled:opacity-60 shadow-lg shadow-indigo-200"
        style={{ background: loading ? '#a5b4fc' : 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
        {loading
          ? (mode === 'signup' ? 'Creating account…' : 'Signing in…')
          : (mode === 'signup' ? '🚀 Start Detecting for Free' : 'Sign In →')}
      </button>

      <p className="text-center text-xs text-gray-400 mt-3">
        By continuing you agree to our{' '}
        <a href="/terms" className="underline hover:text-gray-600">Terms</a> and{' '}
        <a href="/privacy" className="underline hover:text-gray-600">Privacy Policy</a>.
      </p>
    </div>
  );
};

// ─── AUTH GATE (full page, only used if someone lands with no session and we want hard gate — currently unused) ──
const AuthGate = ({ children }) => children;

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
const HomePage = () => {
  const navigate = useNavigate();
  const { open: openAuthModal } = useAuthModal();
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeTab, setActiveTab] = useState('image');
  const [infoTab, setInfoTab] = useState('how');
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [profileDropOpen, setProfileDropOpen] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [onboardingSlide, setOnboardingSlide] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem('authentico_onboarded') && true);
  const [onboardingVisible, setOnboardingVisible] = useState(true);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const touchStartX = useRef(null);

  const dismissOnboarding = () => {
    setOnboardingVisible(false);
    setTimeout(() => setShowOnboarding(false), 350);
    localStorage.setItem('authentico_onboarded', '1');
  };
  const nextSlide = () => {
    if (onboardingSlide < 3) setOnboardingSlide(s => s + 1);
    else dismissOnboarding();
  };
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 50) nextSlide();
    else if (diff < -50 && onboardingSlide > 0) setOnboardingSlide(s => s - 1);
    touchStartX.current = null;
  };

  useEffect(() => {
    if (isLoggedIn()) fetchUserProfile();
    else setProfileLoading(false);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isLoggedIn()) fetchUserProfile();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    };
  }, []);

  const fetchUserProfile = async () => {
    try {
      setProfileLoading(true);
      setProfileError(false);
      const response = await axios.get(`${API}/users/me?t=${Date.now()}`, { ...axiosConfig(), timeout: 12000 });
      setUserProfile(response.data);
    } catch (error) {
      // 401 = session invalid/expired — clear token and prompt login
      if (error.response?.status === 401) {
        clearSession();
        setProfileError(false);
        setUserProfile(null);
      } else {
        console.error('Failed to fetch profile:', error);
        setProfileError(true);
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const getScansDisplay = () => {
    if (!userProfile) return '2/6 texts · 2/4 images';
    const tier = userProfile.subscription_tier;
    const textAvailable = userProfile.scans_available?.text;
    const imgAvailable = userProfile.scans_available?.image;
    const vidAvailable = userProfile.scans_available?.video;
    const purchasedText = userProfile.purchased_scans?.text ?? 0;
    const purchasedImg = userProfile.purchased_scans?.image ?? 0;
    const totalText = (textAvailable === 'unlimited' || textAvailable === Infinity) ? null : (textAvailable ?? 0) + purchasedText;
    const totalImg = (imgAvailable === 'unlimited' || imgAvailable === Infinity) ? null : (imgAvailable ?? 0) + purchasedImg;
    const imgLimit = { free: 4, basic: 35, value_pack: 100, professional: 500 }[tier] ?? 0;
    const textLimit = { free: 6, basic: 90, professional: 1000 }[tier] ?? 0;
    const totalImgLimit = imgLimit + purchasedImg;
    const totalTextLimit = textLimit + purchasedText;
    if (tier === 'free') {
      return `${totalText}/${totalTextLimit} texts · ${totalImg}/${totalImgLimit} images`;
    } else if (tier === 'basic') {
      return `Basic · ${totalText}/${totalTextLimit} texts · ${totalImg}/${totalImgLimit} images`;
    } else if (tier === 'value_pack') {
      return `Value Pack · ∞ texts · ${totalImg}/${totalImgLimit} images · ${vidAvailable ?? 0}/20 docs`;
    } else if (tier === 'professional') {
      return 'Pro · ∞ everything';
    } else {
      return `${totalText} texts · ${totalImg} images`;
    }
  };

  const handleTextAnalysis = async () => {
    if (!isLoggedIn()) { openAuthModal(); return; }
    if (!pastedText || pastedText.trim().length < 10) {
      toast.error('Please enter at least 10 characters');
      return;
    }
    setIsUploading(true);
    try {
      const response = await axios.post(`${API}/detect/text`, { text: pastedText }, axiosConfig());
      toast.success('Analysis complete!');
      setUserProfile(prev => prev ? {
        ...prev,
        scans_available: {
          ...prev.scans_available,
          text: Math.max(0, (prev.scans_available?.text ?? 0) - 1)
        },
        welcome_scans_used: response.data?.is_welcome_scan
          ? (prev.welcome_scans_used ?? 0) + 1
          : (prev.welcome_scans_used ?? 0)
      } : prev);
      navigate('/results', {
        state: {
          result: response.data,
          fileName: 'Pasted Text',
          isWelcomeScan: response.data?.is_welcome_scan,
          welcomeScansRemaining: response.data?.welcome_scans_remaining ?? 0
        }
      });
    } catch (error) {
      if (error.response?.status === 401) {
        clearSession();
        setUserProfile(null);
        toast.error('Session expired — please log in again');
        openAuthModal();
      } else if (error.response?.status === 402) {
        const detail = error.response?.data?.detail || '';
        const isText = detail.toLowerCase().includes('text');
        const isImage = detail.toLowerCase().includes('image');
        const msg = isText
          ? 'No text scans left! You get +2 tomorrow, or earn one now by watching ads.'
          : isImage
          ? 'No image scans left! You get +2 next week, or earn one by watching ads.'
          : 'No scans available! Come back tomorrow or watch ads to earn more.';
        toast.error(msg);
        setTimeout(() => navigate('/pricing'), 4000);
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const [imagePreview, setImagePreview] = useState(null);

  const scanFile = async (file) => {
    if (!isLoggedIn()) { openAuthModal(); return; }
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/detect`, formData, {
        ...axiosConfig(),
        headers: { ...axiosConfig().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Analysis complete!');
      const fileType = response.data?.file_type || 'text';
      setUserProfile(prev => prev ? {
        ...prev,
        scans_available: {
          ...prev.scans_available,
          [fileType === 'image' ? 'image' : 'text']: Math.max(0, (prev.scans_available?.[fileType === 'image' ? 'image' : 'text'] ?? 0) - 1)
        }
      } : prev);
      navigate('/results', { state: { result: response.data, fileName: file.name } });
    } catch (error) {
      if (error.response?.status === 401) {
        clearSession();
        setUserProfile(null);
        toast.error('Session expired — please log in again');
        openAuthModal();
      } else if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || 'No scans available!');
        setTimeout(() => navigate('/pricing'), 4000);
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed');
      }
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setImagePreview(null);
    }
  };

  const onDrop = (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    const file = acceptedFiles[0];
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setImagePreview(url);
    } else if (file.type.startsWith('video/')) {
      // Check if user has video scans
      const tier = userProfile?.subscription_tier;
      if (tier === 'free') {
        toast.error('Document scanning is a premium feature. Upgrade your plan!');
        setTimeout(() => navigate('/pricing'), 4000);
        return;
      }
      setImagePreview(null);
      scanFile(file);
    } else {
      setImagePreview(null);
      scanFile(file);
    }
  };

  const imageAccept = {
    'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
  };
  const documentAccept = {
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/pdf': ['.pdf'],
    'application/octet-stream': ['.pptx', '.docx', '.pdf'],
  };
  const currentAccept = activeTab === 'image' ? imageAccept : activeTab === 'document' ? documentAccept : { 'text/plain': ['.txt'], 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: currentAccept,
    maxSize: 100 * 1024 * 1024,
    onDropRejected: (rejectedFiles) => {
      const file = rejectedFiles[0];
      if (file?.errors?.[0]?.code === 'file-too-large') {
        toast.error(`File too large — maximum size is 100MB. Your file is ${(file.file.size / (1024 * 1024)).toFixed(0)}MB.`);
      } else if (activeTab === 'document') {
        toast.error('Document scanning is a paid feature. Upgrade to Basic or higher to scan PPTX, DOCX, and PDF files.', { duration: 5000, action: { label: 'Upgrade', onClick: () => window.location.href = '/pricing' } });
      } else {
        toast.error('File type not supported.');
      }
    },
    disabled: isUploading
  });

  const startCamera = async (mode = facingMode) => {
    setCameraError(null);
    try {
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setCameraStream(stream);
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      setCameraError('Camera access denied or unavailable.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    setCameraStream(null);
  };

  const flipCamera = () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    startCamera(newMode);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedPhoto(dataUrl);
    stopCamera();
  };

  const analyzePhoto = async () => {
    if (!capturedPhoto) return;
    if (!isLoggedIn()) { openAuthModal(); return; }
    setIsUploading(true);
    try {
      const response = await fetch(capturedPhoto);
      const blob = await response.blob();
      const file = new File([blob], 'camera_photo.jpg', { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', file);
      const result = await axios.post(`${API}/detect`, formData, {
        ...axiosConfig(),
        headers: { ...axiosConfig().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Analysis complete!');
      navigate('/results', { state: { result: result.data, fileName: 'Camera Photo' } });
    } catch (error) {
      if (error.response?.status === 401) {
        clearSession();
        setUserProfile(null);
        toast.error('Session expired — please log in again');
        openAuthModal();
      } else if (error.response?.status === 402) {
        toast.error('No scans available! Upgrade or watch ads.');
        setTimeout(() => navigate('/pricing'), 4000);
      } else {
        toast.error('Analysis failed');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleTabChange = (tab) => {
    if (activeTab === 'camera') stopCamera();
    setActiveTab(tab);
    // Camera starts only when user clicks the button
  };

  if (profileError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <X className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="font-semibold text-lg mb-2">Connection issue</h2>
          <p className="text-muted-foreground text-sm mb-6">Could not reach the server. This may be a slow start — please try again. If this keeps happening, contact <a href="mailto:support@authenticoapp.com" className="text-indigo-600 underline">support@authenticoapp.com</a></p>
          <Button onClick={fetchUserProfile} className="bg-primary text-white rounded-full px-6">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const onboardingSlides = [
    {
      emoji: "👋",
      bg: "from-indigo-600 to-violet-600",
      title: "Welcome to Authentico",
      body: "The AI content detector built for teachers, professionals and anyone who needs to know what's real.",
    },
    {
      emoji: "🤔",
      bg: "from-violet-600 to-purple-600",
      title: "Sound familiar?",
      body: "You suspect the essay is AI-written — but you can't prove it. Students are getting better at hiding it.",
    },
    {
      emoji: "⚡",
      bg: "from-purple-600 to-indigo-600",
      title: "Scan anything in seconds",
      body: "Paste an essay, upload a Word doc, PDF or image. Authentico checks it instantly with a full written breakdown.",
    },
    {
      emoji: "🎁",
      bg: "from-indigo-600 to-blue-600",
      title: "Free to start right now",
      body: "No credit card needed. Your first 2 text scans are powered by our premium AI — on us.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <Toaster position="top-center" richColors />
      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="/logo.png" alt="authentico" className="w-12 h-12 md:w-16 md:h-16 rounded-2xl" />
            <span className="text-lg md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>authentico</span>
          </Link>
          <nav className="flex items-center gap-1.5 md:gap-2">
            <Link to="/history" className="hidden md:inline-flex items-center gap-1 bg-white border border-border px-4 py-2 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-primary transition-colors shadow-sm">📋 History</Link>
            <Link to="/pricing" className="hidden md:inline-flex items-center gap-1 bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors">🎁 Offers</Link>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <Badge className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-1 md:px-4 md:py-2 text-xs font-semibold text-indigo-800 whitespace-nowrap">
                {profileLoading && isLoggedIn() ? (
                  <span>2/6 texts · 2/4 images</span>
                ) : (
                  <>
                    <span className="hidden md:inline">{getScansDisplay()}</span>
                    <span className="md:hidden">
                      {userProfile?.subscription_tier === 'free'
                        ? `${userProfile.scans_available?.text ?? 0}/6 texts · ${userProfile.scans_available?.image ?? 0}/4 images`
                        : userProfile?.subscription_tier === 'value_pack' ? 'Value Pack'
                        : userProfile?.subscription_tier === 'basic' ? 'Basic'
                        : userProfile?.subscription_tier === 'professional' ? 'Pro'
                        : '2/6 texts · 2/4 images'}
                    </span>
                  </>
                )}
              </Badge>
              {!profileLoading && userProfile?.subscription_tier === 'free' && (
                <span className="text-xs text-muted-foreground mt-0.5 md:hidden">+1/day · +2/week</span>
              )}
              {!profileLoading && userProfile && (
                <span className="text-xs text-muted-foreground mt-0.5 hidden md:block">
                  {userProfile.subscription_tier === 'free' && '+1 text/day · +2 images/week'}
                  {userProfile.subscription_tier === 'basic' && `${90 + (userProfile.purchased_scans?.text ?? 0)} texts · ${35 + (userProfile.purchased_scans?.image ?? 0)} images · 5 docs/month`}
                  {userProfile.subscription_tier === 'value_pack' && `∞ texts · ${100 + (userProfile.purchased_scans?.image ?? 0)} images · 20 docs/month`}
                  {userProfile.subscription_tier === 'professional' && `${1000 + (userProfile.purchased_scans?.text ?? 0)} texts · ${500 + (userProfile.purchased_scans?.image ?? 0)} images · 200 docs/month`}
                </span>
              )}
            </div>
            {/* Account avatar with dropdown — desktop only */}
            {isLoggedIn() && (
              <div className="relative hidden md:block">
                <button
                  onClick={() => setProfileDropOpen(o => !o)}
                  className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center shadow-sm hover:bg-indigo-700 transition-colors cursor-pointer"
                  title={getAuthedEmail()}
                >
                  <span className="text-white text-sm font-bold">{(getAuthedEmail() || '?')[0].toUpperCase()}</span>
                </button>
                {profileDropOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setProfileDropOpen(false)} />
                    <div className="absolute right-0 top-11 z-50 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-xs text-gray-400 font-medium">Signed in as</p>
                        <p className="text-sm font-semibold text-gray-800 truncate">{getAuthedEmail()}</p>
                      </div>
                      <button
                        onClick={async () => {
                          try { await axios.post(`${API}/auth/logout`, { device_id: getDeviceId() }, axiosConfig()); } catch {}
                          clearSession();
                          window.location.reload();
                        }}
                        className="w-full text-left px-4 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors"
                      >Sign Out</button>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Account icon mobile — tappable with dropdown */}
            {isLoggedIn() && (
              <div className="relative md:hidden">
                <button
                  onClick={() => setProfileDropOpen(o => !o)}
                  className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center shadow-sm active:scale-95 transition-transform"
                >
                  <span className="text-white text-xs font-bold">{(getAuthedEmail() || '?')[0].toUpperCase()}</span>
                </button>
                {profileDropOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setProfileDropOpen(false)} />
                    <div className="absolute right-0 top-10 z-50 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-xs text-gray-400 font-medium">Signed in as</p>
                        <p className="text-sm font-semibold text-gray-800 truncate">{getAuthedEmail()}</p>
                      </div>
                      <button
                        onClick={async () => {
                          try { await axios.post(`${API}/auth/logout`, { device_id: getDeviceId() }, axiosConfig()); } catch {}
                          clearSession();
                          window.location.reload();
                        }}
                        className="w-full text-left px-4 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors"
                      >🚪 Sign Out</button>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Mobile hamburger */}
            <button
              className="md:hidden ml-2 w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded-xl bg-white border border-border shadow-sm active:scale-95 transition-transform"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Menu"
            >
              <span className={`block w-5 h-0.5 bg-gray-700 transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-5 h-0.5 bg-gray-700 transition-all ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-5 h-0.5 bg-gray-700 transition-all ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </nav>
        </div>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="md:hidden border-t-2 border-primary bg-white shadow-xl px-4 pt-3 pb-5 flex flex-col gap-0.5">
            {/* Header strip */}
            <div className="flex items-center gap-2 px-1 pb-2 mb-1 border-b border-indigo-100">
              <div className="w-1.5 h-5 rounded-full bg-primary" />
              <p className="text-xs font-bold text-primary uppercase tracking-widest">Menu</p>
            </div>

            <Link to="/pricing" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3.5 rounded-2xl bg-primary hover:bg-primary/90 active:bg-primary/80 transition-colors border border-primary shadow-sm">
              <span className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center text-base shadow-sm">🎁</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">Free Scans &amp; Offers</p>
                <p className="text-xs text-white/70">Upgrade or get more scans</p>
              </div>
              <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white/70 font-bold text-base">›</span>
            </Link>

            <div className="px-1 pt-2 pb-0.5">
              <p className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Navigation</p>
            </div>
            <Link to="/history" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3.5 rounded-2xl hover:bg-indigo-50 active:bg-indigo-100 transition-colors border border-indigo-100/60 bg-white">
              <span className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-base shadow-sm">📋</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-800">History</p>
                <p className="text-xs text-gray-400">Your past scans</p>
              </div>
              <span className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-300 font-bold text-base">›</span>
            </Link>

            <div className="flex items-center gap-3 px-1 mt-3 mb-1">
              <div className="h-px bg-indigo-100 flex-1" />
              <p className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Learn</p>
              <div className="h-px bg-indigo-100 flex-1" />
            </div>
            <Link to="/how-it-works" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3.5 rounded-2xl hover:bg-indigo-50 active:bg-indigo-100 transition-colors border border-indigo-100/60 bg-white w-full text-left">
              <span className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-base shadow-sm">⚡</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-800">How It Works</p>
                <p className="text-xs text-gray-400">Step-by-step guide</p>
              </div>
              <span className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-300 font-bold text-base">›</span>
            </Link>
            <Link to="/faq" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3.5 rounded-2xl hover:bg-indigo-50 active:bg-indigo-100 transition-colors border border-indigo-100/60 bg-white w-full text-left">
              <span className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-base shadow-sm">❓</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-800">FAQ</p>
                <p className="text-xs text-gray-400">Common questions answered</p>
              </div>
              <span className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-300 font-bold text-base">›</span>
            </Link>

            {/* Account section */}
            {isLoggedIn() && (
              <>
                <div className="flex items-center gap-3 px-1 mt-3 mb-1">
                  <div className="h-px bg-indigo-100 flex-1" />
                  <p className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Account</p>
                  <div className="h-px bg-indigo-100 flex-1" />
                </div>
                <button
                  onClick={() => setAccountOpen(o => !o)}
                  className="flex items-center gap-3 px-3 py-3.5 rounded-2xl hover:bg-indigo-50 active:bg-indigo-100 transition-colors border border-indigo-100/60 bg-white w-full text-left">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">{(getAuthedEmail() || '?')[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800">My Account</p>
                    <p className="text-xs text-gray-400 truncate">{getAuthedEmail()}</p>
                  </div>
                  <span className={`w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-300 font-bold text-base transition-transform duration-200 ${accountOpen ? 'rotate-90' : ''}`}>›</span>
                </button>
                {accountOpen && (
                  <button
                    onClick={async () => {
                      try { await axios.post(`${API}/auth/logout`, { device_id: getDeviceId() }, axiosConfig()); } catch {}
                      clearSession();
                      window.location.reload();
                    }}
                    className="flex items-center gap-3 px-3 py-3.5 rounded-2xl active:bg-red-100 transition-colors border border-red-100 bg-red-50 w-full text-left">
                    <span className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center text-base">🚪</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-red-600">Sign Out</p>
                      <p className="text-xs text-red-400">You'll need to sign in again</p>
                    </div>
                  </button>
                )}
              </>
            )}

            <div className="mt-2 flex justify-center">
              <p className="text-xs text-indigo-300 font-semibold tracking-wide">authenticoapp.com</p>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 md:py-24">
        <div className="text-center mb-4 md:mb-16">
          <h1 className="text-2xl md:text-6xl font-extrabold tracking-tight mb-2 md:mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Detect AI-Generated Content
          </h1>
          <p className="text-sm md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-3 md:mb-8">
            For teachers, professionals, and anyone who needs to know if content was made by AI.
          </p>
        </div>

        {/* Mobile Offers banner - above scan card */}
        <div className="md:hidden mb-4 max-w-3xl mx-auto">
          <Link to="/pricing" className="flex items-center justify-between bg-primary text-white rounded-2xl px-5 py-3.5 active:scale-95 transition-transform shadow-sm">
            <div>
              <p className="font-bold text-sm">🎁 Free Scans &amp; Special Offers</p>
              <p className="text-xs text-white/75 mt-0.5">Upgrade or grab a scan pack for more scans</p>
            </div>
            <span className="text-white/70 text-xl ml-3">›</span>
          </Link>
        </div>

        {/* Welcome scan banner — free users only, while they have welcome scans left */}
        {userProfile?.subscription_tier === 'free' && (userProfile?.welcome_scans_used ?? 0) < 2 && (
          <div className="max-w-3xl mx-auto mb-3">
            <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-2xl px-4 py-3">
              <span className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center text-sm flex-shrink-0">⚡</span>
              <div className="flex-1">
                <p className="text-xs font-bold text-indigo-800">
                  {(userProfile?.welcome_scans_used ?? 0) === 0
                    ? '🎁 Your first 2 text scans are powered by Claude Sonnet 4 — our premium model'
                    : '⚡ 1 premium welcome scan remaining — make it count'}
                </p>
              </div>
            </div>
          </div>
        )}

        <Card className="max-w-3xl mx-auto bg-white border-t-2 border-primary md:border-t md:border-border/50 shadow-lg rounded-3xl p-5 md:p-12">

          {/* Mobile card header strip */}
          <div className="flex items-center gap-2 mb-5 pb-4 border-b border-indigo-100 md:hidden">
            <div className="w-1.5 h-5 rounded-full bg-primary" />
            <p className="text-xs font-bold text-primary uppercase tracking-widest">Scan Content</p>
          </div>

          {/* Mobile tabs — 2x2 card grid */}
          <div className="grid grid-cols-2 gap-2 mb-5 md:hidden">
            {[
              { id: 'image',    label: 'Image',      sub: 'Scan an image file',   emoji: '🖼️' },
              { id: 'paste',    label: 'Paste Text',  sub: 'Essays & text',        emoji: '📝' },
              { id: 'document', label: 'Document',    sub: 'PDF, DOCX, PPTX',      emoji: '📄' },
              { id: 'camera',   label: 'Take Photo',  sub: 'Use your camera',      emoji: '📷' },
            ].map(tab => (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2.5 p-3 rounded-2xl border transition-all active:scale-95 text-left ${
                  activeTab === tab.id
                    ? 'bg-indigo-100 border-indigo-300 shadow-sm'
                    : 'bg-white border-indigo-100 hover:bg-indigo-50'
                }`}>
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${
                  activeTab === tab.id ? 'bg-indigo-200' : 'bg-indigo-100'
                }`}>{tab.emoji}</span>
                <div>
                  <p className={`text-xs font-bold leading-tight ${activeTab === tab.id ? 'text-indigo-800' : 'text-gray-800'}`}>{tab.label}</p>
                  <p className={`text-xs leading-tight mt-0.5 ${activeTab === tab.id ? 'text-indigo-500' : 'text-gray-400'}`}>{tab.sub}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Desktop tabs — flat underline */}
          <div className="relative mb-6 hidden md:block">
            <div className="flex gap-1 border-b border-border">
            {[
              { id: 'image', label: 'Image', icon: <ImageIcon className="w-4 h-4 inline mr-1" /> },
              { id: 'paste', label: 'Paste Text', icon: <FileText className="w-4 h-4 inline mr-1" /> },
              { id: 'document', label: 'Document', icon: <FileText className="w-4 h-4 inline mr-1" /> },
              { id: 'camera', label: 'Take Photo', icon: <Camera className="w-4 h-4 inline mr-1" /> },
            ].map(tab => (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={`pb-3 px-3 py-2 font-semibold transition-colors text-sm text-center ${activeTab === tab.id ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                {tab.icon}{tab.label}
              </button>
            ))}
            </div>
          </div>

          {/* Image Tab */}
          {activeTab === 'image' && (
            <div className="space-y-4">
              {imagePreview ? (
                <div className="space-y-4">
                  <div className="relative rounded-2xl overflow-hidden border border-border">
                    <img src={imagePreview} alt="Preview" className="w-full max-h-72 object-contain bg-gray-50" />
                    <button
                      onClick={() => { setImagePreview(null); setSelectedFile(null); }}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors">
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground text-center truncate">{selectedFile?.name}</p>
                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setImagePreview(null); setSelectedFile(null); }}>
                      <X className="w-4 h-4 mr-2" />Cancel
                    </Button>
                    <Button onClick={() => scanFile(selectedFile)} disabled={isUploading} className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-full font-semibold">
                      {isUploading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>Analyzing...</> : <><Zap className="w-4 h-4 mr-2" />Scan this image</>}
                    </Button>
                  </div>
                </div>
              ) : (
                <div {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-8 md:p-12 text-center transition-all cursor-pointer ${isDragActive ? 'border-primary bg-indigo-50' : 'border-indigo-200 hover:border-primary hover:bg-indigo-50/30'} ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input {...getInputProps()} />
                  {isUploading ? (
                    <div className="space-y-4">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary"></div>
                      </div>
                      <p className="text-base font-bold text-gray-800">Analyzing...</p>
                      <p className="text-sm text-muted-foreground">{selectedFile?.name}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-100 flex items-center justify-center shadow-sm">
                        <ImageIcon className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <p className="text-base font-bold text-gray-800 mb-1">{isDragActive ? 'Drop your image here' : 'Drag & drop or click to upload'}</p>
                        <p className="text-xs text-muted-foreground">PNG, JPG, JPEG, WEBP · Max 100MB</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Document Tab */}
          {activeTab === 'document' && (() => {
            const docScans = userProfile?.subscription_tier === 'free' ? 0 : (userProfile?.scans_available?.video ?? 0);
            const hasDocAccess = userProfile?.subscription_tier !== 'free';
            return (
              <div className="relative">
                {!hasDocAccess && (
                  <div className="absolute inset-0 z-10 rounded-2xl bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4 p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                      <span className="text-2xl">🔒</span>
                    </div>
                    <div>
                      <p className="text-lg font-bold mb-1">Paid Feature</p>
                      <p className="text-sm text-muted-foreground">Document scanning (PPTX, DOCX, PDF) is available on Basic and higher plans.</p>
                    </div>
                    <a href="/pricing" className="bg-primary text-white rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-primary/90 transition-colors">Upgrade to unlock</a>
                  </div>
                )}
                <div {...getRootProps()}
                  className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${isDragActive ? 'border-primary bg-indigo-50' : 'border-border hover:border-primary hover:bg-indigo-50/30'} ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input {...getInputProps()} />
                  {isUploading ? (
                    <div className="space-y-4">
                      <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                      <p className="text-lg font-semibold">Analyzing...</p>
                      <p className="text-sm text-muted-foreground">{selectedFile?.name}</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="w-20 h-20 mx-auto rounded-full bg-indigo-100 flex items-center justify-center">
                        <FileText className="w-10 h-10 text-primary" />
                      </div>
                      <div>
                        <p className="text-xl font-semibold mb-2">{isDragActive ? 'Drop your document here' : 'Drag & drop or click to upload'}</p>
                        <p className="text-sm font-medium text-foreground mb-1">Student Essays · Work Reports · Any Document</p>
                        <p className="text-xs text-muted-foreground">PPTX, DOCX, PDF · Max 100MB</p>
                      </div>
                      <div className="flex items-center justify-center gap-8 pt-4">
                        <div className="flex flex-col items-center gap-2"><Presentation className="w-8 h-8 text-orange-400" /><span className="text-xs text-muted-foreground">PPTX</span></div>
                        <div className="flex flex-col items-center gap-2"><FileText className="w-8 h-8 text-blue-400" /><span className="text-xs text-muted-foreground">DOCX</span></div>
                        <div className="flex flex-col items-center gap-2"><FileImage className="w-8 h-8 text-red-400" /><span className="text-xs text-muted-foreground">PDF</span></div>

                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Paste Text Tab */}
          {activeTab === 'paste' && (
            <div className="space-y-4">
              <textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                placeholder="Paste your text, essay, article, etc. here to check if it's AI-generated..."
                className="w-full h-64 p-4 border-2 border-border rounded-2xl resize-none focus:border-primary focus:outline-none transition-colors"
                style={{ color: '#0f172a', backgroundColor: '#f8fafc' }}
                disabled={isUploading}
              />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{pastedText.length} characters · Minimum 10 required</p>
                    <p className="text-xs text-muted-foreground mt-0.5">💡 Works for essays, emails, articles, etc.</p>
                  </div>
                  <Button onClick={handleTextAnalysis} disabled={isUploading || pastedText.length < 10}
                    className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-3 font-semibold ml-4 flex-shrink-0"
                    style={{ color: '#000000', backgroundColor: '#ffffff' }}>
                    {isUploading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2 inline-block"></div>Analyzing...</>
                    ) : (
                      <><Zap className="w-4 h-4 inline mr-2" />Analyze Text</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Camera Tab */}
          {activeTab === 'camera' && (
            <div className="space-y-4">
              {cameraError ? (
                <div className="text-center py-8 text-red-500">
                  <p>{cameraError}</p>
                  <Button onClick={() => startCamera()} className="mt-4">Try Again</Button>
                </div>
              ) : capturedPhoto ? (
                <div className="space-y-4">
                  <img src={capturedPhoto} alt="Captured" className="w-full rounded-2xl" />
                  <div className="flex gap-3">
                    <Button onClick={() => { setCapturedPhoto(null); startCamera(); }} variant="outline" className="flex-1">
                      <RefreshCw className="w-4 h-4 mr-2" />Retake
                    </Button>
                    <Button onClick={analyzePhoto} disabled={isUploading}
                      className="flex-1 bg-primary text-white" style={{ color: '#000000', backgroundColor: '#6366f1' }}>
                      {isUploading ? 'Analyzing...' : <><Zap className="w-4 h-4 mr-2" />Analyze Photo</>}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative bg-black rounded-2xl overflow-hidden aspect-video">
                    <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                    {!cameraStream && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80">
                        <p className="text-white text-sm px-4 text-center">Point your camera at text or an image to scan it</p>
                        <p className="text-white/60 text-xs">Uses 1 image scan per photo</p>
                        <Button onClick={() => startCamera()} className="bg-primary text-white mt-2">
                          <Camera className="w-5 h-5 mr-2" />Enable Camera
                        </Button>
                      </div>
                    )}
                  </div>
                  {cameraStream && (
                    <div className="flex items-center justify-center gap-4">
                      <Button variant="outline" onClick={flipCamera} className="rounded-full p-3">
                        <RefreshCw className="w-5 h-5" />
                      </Button>
                      <button onClick={capturePhoto}
                        className="w-16 h-16 rounded-full bg-white border-4 border-primary flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                        <div className="w-12 h-12 rounded-full bg-primary" />
                      </button>
                      <Button variant="outline" onClick={stopCamera} className="rounded-full p-3">
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  )}
                  <p className="text-center text-xs text-muted-foreground">📷 Uses 1 image scan per photo · For best results on text, use Paste Text instead</p>
                </div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-5 flex items-start gap-3 p-4 rounded-2xl bg-indigo-50 border border-indigo-100">
            <span className="text-lg flex-shrink-0">ℹ️</span>
            <p className="text-xs text-indigo-700 leading-relaxed">
              <strong>Heads up:</strong> Authentico is highly accurate, but like any detection tool it can occasionally get it wrong. Use your own judgment alongside the results.
            </p>
          </div>
        </Card>

        {/* Features */}
        <div className="hidden md:grid grid-cols-3 gap-8 mt-16">
          <Card className="bg-white border border-border/50 rounded-3xl p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-indigo-100 flex items-center justify-center mb-4"><Zap className="w-6 h-6 text-primary" /></div>
            <h3 className="text-xl font-bold mb-2">Lightning Fast</h3>
            <p className="text-muted-foreground text-sm">Get results in seconds with our optimized detection engine</p>
          </Card>
          <Card className="bg-white border border-border/50 rounded-3xl p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-indigo-100 flex items-center justify-center mb-4"><Check className="w-6 h-6 text-primary" /></div>
            <h3 className="text-xl font-bold mb-2">Highly Accurate</h3>
            <p className="text-muted-foreground text-sm">Advanced algorithms trained on millions of samples</p>
          </Card>
          <Card className="bg-white border border-border/50 rounded-3xl p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-indigo-100 flex items-center justify-center mb-4"><FileText className="w-6 h-6 text-primary" /></div>
            <h3 className="text-xl font-bold mb-2">Multiple Formats</h3>
            <p className="text-muted-foreground text-sm">Text, images, and documents (PPTX, DOCX, PDF)</p>
          </Card>
        </div>

        {/* How It Works / FAQ — tabbed (desktop always, mobile only when opened from menu) */}
        <div id="info-tabs" className={`mt-10 mb-12 max-w-3xl mx-auto px-2 ${mobileInfoOpen ? 'block' : 'hidden'} md:block`}>
          {/* Tab switcher */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-gray-100 rounded-full p-1 gap-1 w-full max-w-xs">
              {[{ id: 'how', label: '⚡ How It Works' }, { id: 'faq', label: '❓ FAQ' }].map(t => (
                <button
                  key={t.id}
                  onClick={() => setInfoTab(t.id)}
                  className={`flex-1 py-2.5 px-4 rounded-full text-sm font-semibold transition-all active:scale-95 ${
                    infoTab === t.id
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* How It Works tab */}
          {infoTab === 'how' && (
            <div className="space-y-6">
              {[
                { step: '1', icon: '📋', title: 'Choose your content type', body: 'Pick from Image, Paste Text, Document, or Take Photo. Each mode is optimized for that content type — text uses linguistic analysis, images use visual pattern detection.' },
                { step: '2', icon: '🚀', title: 'Upload or paste your content', body: 'Drop in a file, paste an essay, or point your camera. Authentico supports PNG, JPG, WEBP images, plus PPTX, DOCX, and PDF documents on paid plans.' },
                { step: '3', icon: '🔍', title: 'AI analyzes the content', body: 'Our engine — powered by Claude Sonnet for paid users and Sightengine for images — scans for AI patterns, linguistic markers, and statistical signatures in seconds.' },
                { step: '4', icon: '✅', title: 'Get a clear verdict', body: 'You receive a percentage score, a Likely AI / Likely Human verdict, and a detailed breakdown of the specific indicators found — so you always understand the why.' },
              ].map(({ step, icon, title, body }) => (
                <div key={step} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-lg font-black text-primary">
                    {step}
                  </div>
                  <div>
                    <h3 className="font-bold text-base mb-1">{icon} {title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* FAQ tab */}
          {infoTab === 'faq' && (
            <div className="space-y-5">
              {[
                { q: 'What is AI content detection?', a: 'AI content detection analyzes text, images, or documents to determine whether they were created by a human or generated by a model like ChatGPT, GPT-4, or Midjourney. Authentico detects patterns, structure, and linguistic markers that indicate AI authorship.' },
                { q: 'How accurate is Authentico?', a: 'Authentico uses Claude — one of the most advanced AI models available — for text analysis, and Sightengine for images. No detector is 100% accurate, but we provide a detailed confidence breakdown so you always understand the reasoning. Use it as one tool among several for important decisions.' },
                { q: 'Can teachers use it to catch AI-written essays?', a: 'Yes — Authentico is built with educators in mind. Paste essay text directly, or upload student submissions as Word documents, PDFs, or PowerPoint presentations. You get a clear verdict plus an explanation of the patterns detected.' },
                { q: 'What file types are supported?', a: 'Text scanning via paste, image scanning for PNG, JPG, JPEG, WEBP, and document scanning for PPTX, DOCX, PDF (paid plans). You can also use your device camera to capture and scan content directly.' },
                { q: 'Is Authentico free?', a: 'Yes — the free tier includes 1 text scan per day and 2 image scans per week. Paid plans start at €5.99/month and add document scanning, higher limits, and Claude Sonnet for premium text analysis. One-time scan packs are also available.' },
                { q: 'Is my content kept private?', a: 'Authentico does not store the content you submit. Scans are processed securely and used only to generate your result. No account registration required — you\'re identified by an anonymous browser ID only.' },
              ].map(({ q, a }, i) => (
                <div key={i} className="border border-border/60 rounded-2xl p-5 bg-white">
                  <h3 className="font-bold text-sm mb-2 text-gray-900">{q}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          )}
        </div>

      </main>
      <footer className="border-t border-border/50 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 flex gap-6 justify-center text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
          <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
        </div>
      </footer>

      {/* ── ONBOARDING ── */}
      {showOnboarding && (
        <div
          className="fixed inset-0 z-50 flex flex-col"
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
          style={{ opacity: onboardingVisible ? 1 : 0, transition: 'opacity 0.3s ease' }}
        >
          {/* Gradient top half */}
          <div className={`flex-1 bg-gradient-to-br ${onboardingSlides[onboardingSlide].bg} flex flex-col items-center justify-center px-8 pt-12 pb-8`}
            style={{ transition: 'background 0.4s ease' }}>
            {/* Skip button */}
            {onboardingSlide < 3 && (
              <button onClick={dismissOnboarding} className="absolute top-6 right-6 text-white/70 text-sm font-medium">Skip</button>
            )}
            {/* Logo */}
            <img src="/logo.png" alt="authentico" className="w-16 h-16 rounded-2xl shadow-lg mb-6" />
            {/* Big emoji */}
            <div className="text-8xl mb-6 select-none">{onboardingSlides[onboardingSlide].emoji}</div>
          </div>
          {/* White bottom half */}
          <div className="bg-white px-8 pt-8 pb-10 flex flex-col" style={{ minHeight: '45%' }}>
            <h1 className="text-2xl font-extrabold text-gray-900 mb-3 text-center leading-tight">
              {onboardingSlides[onboardingSlide].title}
            </h1>
            <p className="text-base text-gray-500 text-center leading-relaxed mb-8 flex-1">
              {onboardingSlides[onboardingSlide].body}
            </p>
            {/* Dots */}
            <div className="flex justify-center gap-2 mb-6">
              {onboardingSlides.map((_, i) => (
                <button key={i} onClick={() => setOnboardingSlide(i)}
                  style={{ width: i === onboardingSlide ? 28 : 8, height: 8, borderRadius: 4, background: i === onboardingSlide ? '#6366f1' : '#e0e7ff', transition: 'all 0.3s ease' }} />
              ))}
            </div>
            {/* Button */}
            <button onClick={nextSlide}
              className="w-full bg-primary text-white font-bold rounded-2xl py-4 text-base active:scale-95 transition-transform shadow-md">
              {onboardingSlide < 3 ? 'Next →' : 'Get Started 🎉'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── RESULTS PAGE ─────────────────────────────────────────────────────────────
const ResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const result = location.state?.result;
  const fileName = location.state?.fileName;
  const isWelcomeScan = location.state?.isWelcomeScan;
  const welcomeScansRemaining = location.state?.welcomeScansRemaining ?? 0;
  const [showWelcomePopup, setShowWelcomePopup] = React.useState(false);

  React.useEffect(() => {
    if (isWelcomeScan) {
      setTimeout(() => {
        toast(welcomeScansRemaining > 0
          ? `⚡ Scanned with Claude Sonnet 4 · ${welcomeScansRemaining} welcome scan remaining`
          : '⚡ Last welcome scan used — upgrade to keep premium quality', {
          duration: 5000,
          style: { background: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3', fontWeight: '600', fontSize: '13px' }
        });
      }, 800);
    }
  }, []);

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">No results to display</p>
          <Button onClick={() => navigate('/')}>Go Back Home</Button>
        </div>
      </div>
    );
  }

  const aiProbability = Math.round(result.confidence * 100); // confidence is always AI probability
  const isAI = aiProbability > 65;
  const isProbablyAI = aiProbability > 55 && aiProbability <= 65;
  const isUncertain = aiProbability >= 45 && aiProbability <= 55;
  const isProbablyHuman = aiProbability >= 35 && aiProbability < 45;
  const isHuman = aiProbability < 35;

  const getVerdict = () => {
    if (isAI) return { label: 'Likely AI-Generated', color: 'red', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', num: 'text-red-600', iconColor: 'text-red-500', dotColor: 'text-red-500', barColor: '#ef4444', sectionBg: 'bg-red-50 border-red-200', sectionTitle: 'Detected AI Indicators' };
    if (isProbablyAI) return { label: 'Possibly AI-Generated', color: 'orange', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', num: 'text-orange-600', iconColor: 'text-orange-500', dotColor: 'text-orange-500', barColor: '#f97316', sectionBg: 'bg-orange-50 border-orange-200', sectionTitle: 'Possible AI Indicators' };
    if (isUncertain) return { label: 'Uncertain', color: 'amber', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', num: 'text-amber-600', iconColor: 'text-amber-500', dotColor: 'text-amber-500', barColor: '#f59e0b', sectionBg: 'bg-amber-50 border-amber-200', sectionTitle: 'Analysis Observations' };
    if (isProbablyHuman) return { label: 'Possibly Human-Created', color: 'lime', bg: 'bg-lime-50', border: 'border-lime-200', text: 'text-lime-700', num: 'text-lime-600', iconColor: 'text-lime-500', dotColor: 'text-lime-500', barColor: '#84cc16', sectionBg: 'bg-lime-50 border-lime-200', sectionTitle: 'Human Authenticity Signals' };
    return { label: 'Likely Human-Created', color: 'green', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', num: 'text-green-600', iconColor: 'text-green-500', dotColor: 'text-green-500', barColor: '#22c55e', sectionBg: 'bg-green-50 border-green-200', sectionTitle: 'Human Authenticity Signals' };
  };

  const verdict = getVerdict();
  const flaws = result.flaws || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <Toaster position="top-center" richColors />
      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="authentico" className="w-16 h-16 rounded-2xl" />
            <span className="text-2xl font-bold tracking-tight">authentico</span>
          </Link>
          <Button onClick={() => navigate('/')} className="bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-full px-4 py-2 text-sm font-semibold shadow-sm">← New Scan</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Analysis Complete</h1>
          <p className="text-muted-foreground">{fileName}</p>
        </div>

        {/* Verdict card */}
        <div className={`rounded-3xl p-8 md:p-12 mb-6 text-center border-2 ${verdict.bg} ${verdict.border}`}>
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${verdict.bg}`}>
            {isAI || isProbablyAI ? <Zap className={`w-10 h-10 ${verdict.iconColor}`} /> : isUncertain ? <Info className={`w-10 h-10 ${verdict.iconColor}`} /> : <Check className={`w-10 h-10 ${verdict.iconColor}`} />}
          </div>
          <h2 className={`text-3xl font-extrabold mb-1 ${verdict.text}`}>{verdict.label}</h2>
          <p className="text-sm text-muted-foreground mb-4">{fileName}</p>
          <p className={`text-7xl font-black mb-1 ${verdict.num}`}>{aiProbability}%</p>
          <p className="text-muted-foreground">probability of AI generation</p>

          {/* Gauge bar */}
          <div className="mt-6 max-w-sm mx-auto">
            <div className="flex items-center justify-between mb-1 text-xs text-muted-foreground">
              <span>Human</span><span>Uncertain</span><span>AI</span>
            </div>
            <div className="relative h-3 bg-white/70 rounded-full overflow-hidden">
              <div className="absolute h-full bg-gradient-to-r from-green-400 via-amber-400 to-red-500 w-full opacity-30 rounded-full" />
              <div className="absolute h-full rounded-full transition-all duration-700"
                style={{ width: `${aiProbability}%`, background: verdict.barColor }} />
            </div>
          </div>
        </div>

        <Card className="bg-white border border-border/50 shadow-lg rounded-3xl p-8 md:p-12 mb-8">

          {/* Analysis indicators */}
          {flaws && flaws.length > 0 && (
            <div className={`mb-8 rounded-2xl p-6 border ${verdict.sectionBg}`}>
              <div className="flex items-center gap-2 mb-4">
                {isAI || isProbablyAI ? <Zap className={`w-5 h-5 ${verdict.iconColor}`} /> : isUncertain ? <Info className={`w-5 h-5 ${verdict.iconColor}`} /> : <Check className={`w-5 h-5 ${verdict.iconColor}`} />}
                <h3 className="font-bold text-lg">{verdict.sectionTitle}</h3>
                {result.is_premium_image_analysis && (
                  <span className="ml-auto text-xs font-bold text-primary bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-1">⚡ Premium</span>
                )}
              </div>
              <ul className="space-y-2">
                {flaws.map((flaw, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className={`mt-1 ${verdict.dotColor}`}>•</span><span>{flaw}</span>
                  </li>
                ))}
              </ul>
              {result.file_type === 'image' && !result.is_premium_image_analysis && (
                <Link to="/pricing" className="mt-4 flex items-center gap-2 bg-white border border-indigo-200 rounded-2xl px-4 py-3 hover:bg-indigo-50 transition-colors">
                  <span className="text-lg">⚡</span>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-gray-800">Get a detailed AI breakdown of every image scan</p>
                    <p className="text-xs text-gray-400">Upgrade to see exactly what triggered the detection</p>
                  </div>
                  <span className="text-indigo-300 text-base">›</span>
                </Link>
              )}
            </div>
          )}

          {/* Scans remaining */}
          {result.scans_remaining && (
            <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-100 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-indigo-900">Scans Remaining</span>
                <span className="font-bold text-indigo-600">
                  {result.scans_remaining.remaining === 'unlimited' ? '∞ Unlimited' : `${result.scans_remaining.remaining}/${result.scans_remaining.limit}`}
                </span>
              </div>
              <p className="text-xs text-indigo-600 mt-1">
                {result.scans_remaining.type === 'daily' && 'Resets daily'}
                {result.scans_remaining.type === 'weekly' && 'Resets weekly'}
                {result.scans_remaining.type === 'monthly' && 'Resets monthly'}
                {result.scans_remaining.type === 'unlimited' && 'No limits!'}
              </p>
            </div>
          )}

          <div className="flex items-start gap-3 p-4 rounded-xl bg-indigo-50/50 border border-indigo-100">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              This result is based on AI analysis and should be used as guidance. No detection system is 100% accurate.
            </p>
          </div>
        </Card>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={() => navigate('/')} className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
            Scan Another File
          </Button>
          <Button onClick={() => { navigator.clipboard?.writeText(`Authentico result: ${result.result} — ${aiProbability}% AI probability`); toast && toast.success('Result copied!'); }} variant="outline" className="rounded-full px-8 py-6 font-semibold border-border hover:bg-gray-50">
            Copy Result
          </Button>
        </div>
      </main>
      {/* Welcome gift popup */}
      {showWelcomePopup && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Purple header */}
            <div className="bg-primary px-6 pt-6 pb-8 text-center relative">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-3 text-3xl">✨</div>
              <h2 className="text-xl font-extrabold text-white mb-1">Welcome Gift</h2>
              <p className="text-white/80 text-sm">This scan was powered by</p>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 mt-2">
                <span className="text-white font-bold text-sm">⚡ Claude Sonnet 4 — Premium</span>
              </div>
            </div>
            {/* Content */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600 text-center leading-relaxed mb-1">
                {result?.welcome_scans_remaining > 0
                  ? `You have ${result.welcome_scans_remaining} premium welcome scan left.`
                  : `That was your last premium welcome scan.`}
              </p>
              <p className="text-xs text-gray-400 text-center mb-5">
                Upgrade to keep getting the most accurate results on every scan.
              </p>
              <Link to="/pricing" onClick={() => setShowWelcomePopup(false)}
                className="flex items-center justify-center gap-2 w-full bg-primary text-white font-bold rounded-2xl py-3.5 text-sm active:scale-95 transition-transform shadow-sm mb-3">
                🎁 See Plans &amp; Offers
              </Link>
              <button onClick={() => setShowWelcomePopup(false)}
                className="w-full text-center text-xs text-gray-400 py-2 active:text-gray-600">
                Continue with free tier
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userProfile, setUserProfile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [showCancelBanner, setShowCancelBanner] = useState(false);
  const [teamNotifyEmail, setTeamNotifyEmail] = useState('');
  const [teamNotifySubmitted, setTeamNotifySubmitted] = useState(false);
  const [textAdProgress, setTextAdProgress] = useState(0); // ads watched toward next text scan
  const [imageAdProgress, setImageAdProgress] = useState(0); // ads watched toward next image scan
  const [textAdsEarnedToday, setTextAdsEarnedToday] = useState(0); // scans earned today
  const [imageAdsEarnedToday, setImageAdsEarnedToday] = useState(0);

  useEffect(() => {
    if (isLoggedIn()) fetchUserProfile();
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, [searchParams]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/me?t=${Date.now()}`, axiosConfig());
      setUserProfile(response.data);
    } catch (error) {
      if (error.response?.status !== 401) console.error('Failed to fetch profile:', error);
    }
  };

  const getScansDisplay = () => {
    if (!userProfile) return '...';
    const tier = userProfile.subscription_tier;
    const textAvailable = userProfile.scans_available?.text;
    const imgAvailable = userProfile.scans_available?.image;
    const vidAvailable = userProfile.scans_available?.video;
    const purchasedText = userProfile.purchased_scans?.text ?? 0;
    const purchasedImg = userProfile.purchased_scans?.image ?? 0;
    const totalText = (textAvailable === 'unlimited' || textAvailable === Infinity) ? null : (textAvailable ?? 0) + purchasedText;
    const totalImg = (imgAvailable === 'unlimited' || imgAvailable === Infinity) ? null : (imgAvailable ?? 0) + purchasedImg;
    const imgLimit = { free: 4, basic: 35, value_pack: 100, professional: 500 }[tier] ?? 0;
    const textLimit = { free: 6, basic: 90, professional: 1000 }[tier] ?? 0;
    const totalImgLimit = imgLimit + purchasedImg;
    const totalTextLimit = textLimit + purchasedText;
    if (tier === 'free') return `Free · ${totalText}/${totalTextLimit} texts · ${totalImg}/${totalImgLimit} images`;
    if (tier === 'basic') return `Basic · ${totalText}/${totalTextLimit} texts · ${totalImg}/${totalImgLimit} images`;
    if (tier === 'value_pack') return `Value Pack · ∞ texts · ${totalImg}/${totalImgLimit} images · ${vidAvailable ?? 0}/20 docs`;
    if (tier === 'professional') return `Pro · ${totalText}/${totalTextLimit} texts · ${totalImg}/${totalImgLimit} images · $(userProfile?.scans_available?.video ?? 0)/200 docs`;
    return 'Free';
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { toast.error('Payment verification timed out'); return; }
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Scans added.');
        await fetchUserProfile();
        window.history.replaceState({}, '', '/pricing');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) { console.error('Payment status check failed:', error); }
  };

  const handlePurchase = async (packageType) => {
    setIsProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/session`, { package_type: packageType, origin_url: originUrl }, axiosConfig());
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOneTimePurchase = async (packageType) => {
    setIsProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/purchase/one-time`, { package_type: packageType, origin_url: originUrl }, axiosConfig());
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTeamNotify = () => {
    if (!teamNotifyEmail) return;
    // Store email locally for now - can wire to backend later
    const existing = JSON.parse(localStorage.getItem('teamNotifyEmails') || '[]');
    existing.push({ email: teamNotifyEmail, date: new Date().toISOString() });
    localStorage.setItem('teamNotifyEmails', JSON.stringify(existing));
    setTeamNotifySubmitted(true);
  };

  const handleWatchAd = async (scanType) => {
    try {
      const response = await axios.post(`${API}/ads/watch`, { user_email: getUserEmail(), scan_type: scanType });
      const data = response.data;

      if (data.daily_limit_reached && !data.scan_granted) {
        toast.error(data.message);
        return;
      }

      if (data.scan_granted) {
        toast.success(data.message);
        if (scanType === 'text') {
          setTextAdProgress(0);
          setTextAdsEarnedToday(prev => prev + 1);
        } else {
          setImageAdProgress(0);
          setImageAdsEarnedToday(prev => prev + 1);
        }
      } else {
        toast.info(data.message);
        if (scanType === 'text') setTextAdProgress(data.ads_watched || 0);
        else setImageAdProgress(data.ads_watched || 0);
      }
      await fetchUserProfile();
    } catch (error) { toast.error('Failed to process ad'); }
  };

  const handleCancelSubscription = async () => {
    try {
      await axios.post(`${API}/cancel-subscription`, {}, axiosConfig());
      toast.success('Subscription cancelled.');
      setCancelDialogOpen(false);
      setShowCancelBanner(true);
      await fetchUserProfile();
    } catch (error) {
      toast.error('Cancellation failed. Please try again.');
    }
  };

  const isSubscribed = userProfile && userProfile.subscription_tier !== 'free';

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <Toaster position="top-center" richColors />

      {showCancelBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between">
          <p className="text-amber-800 text-sm font-medium">⚠️ Your subscription has been cancelled. Access continues until the end of your billing period.</p>
          <button onClick={() => setShowCancelBanner(false)} className="text-amber-600 hover:text-amber-800 ml-4">✕</button>
        </div>
      )}

      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="/logo.png" alt="authentico" className="w-12 h-12 md:w-16 md:h-16 rounded-2xl" />
            <span className="text-lg md:text-2xl font-bold tracking-tight">authentico</span>
          </Link>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button onClick={() => navigate('/')} className="bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-full px-3 py-1.5 text-xs md:text-sm font-semibold shadow-sm">← Back</Button>
            <Badge className="hidden sm:inline-flex items-center rounded-full bg-indigo-100 px-3 py-1.5 text-xs md:text-sm font-semibold text-indigo-800">
              {getScansDisplay()}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-6xl font-extrabold tracking-tight mb-4">Choose Your Plan</h1>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-4">
            Free: 1 text/day · 2 images/week. Upgrade for more.
          </p>
          <p className="text-xs text-muted-foreground max-w-xl mx-auto mb-6">Image scans always use Claude Sonnet 4. Paid plans unlock premium text detection.</p>
          <a href="#top-ups" className="inline-flex items-center gap-2 text-sm font-medium text-primary bg-primary/8 px-4 py-2 rounded-full hover:bg-primary/15 transition-colors">
            💳 Not into subscriptions? We also have one-time top-ups ↓
          </a>
        </div>

        {/* Earn section — hidden until AdSense approved, restore by uncommenting below */}
        {/* RESTORE POINT: Coming Soon ad earn section
        <div className="max-w-4xl mx-auto mb-12">
          <div className="relative rounded-3xl border border-dashed border-gray-300 bg-gray-50/50 p-8">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-gray-800 text-white text-xs font-bold px-4 py-1 rounded-full tracking-widest uppercase">Coming Soon</span>
            </div>
            <div className="grid md:grid-cols-2 gap-6 opacity-40 pointer-events-none select-none">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200 rounded-3xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Text Scan</h3>
                <p className="text-muted-foreground mb-6">Watch 2 ads to earn 1 free text scan</p>
                <Button disabled className="bg-blue-600 text-white rounded-full px-8 py-3 font-semibold w-full opacity-50 cursor-not-allowed">
                  <Video className="w-4 h-4 mr-2" />Watch Ad
                </Button>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200 rounded-3xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-purple-500 rounded-full flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Image Scan</h3>
                <p className="text-muted-foreground mb-6">Watch 3 ads to earn 1 free image scan</p>
                <Button disabled className="bg-purple-600 text-white rounded-full px-8 py-3 font-semibold w-full opacity-50 cursor-not-allowed">
                  <Video className="w-4 h-4 mr-2" />Watch Ad
                </Button>
              </Card>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">Earn free scans by watching ads — available soon</p>
          </div>
        </div>
        END RESTORE POINT */}

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Basic */}
          <Card className="bg-white border border-border/50 rounded-3xl p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">Basic</h3>
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full mb-1" style={{whiteSpace:"nowrap"}}>⚡ Premium text detection — Claude Sonnet 4</span><br/>
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full mb-2" style={{whiteSpace:"nowrap"}}>🖼️ Detailed AI breakdown of every image scan</span>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold">€5.99</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8">
              {['90 text scans/month', '35 image scans/month', '5 document scans/month (PPTX, DOCX, PDF)', 'Detects humanized AI text', '🖼️ Detailed AI breakdown of every image scan', 'Perfect for teachers'].map(f => (
                <li key={f} className="flex items-start gap-3"><Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" /><span className="text-muted-foreground">{f}</span></li>
              ))}
            </ul>
            <Button onClick={() => handlePurchase('basic')} disabled={isProcessing} className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-5 font-bold text-lg w-full">
              {isProcessing ? 'Processing...' : 'Get Basic'}
            </Button>
          </Card>

          {/* Value Pack */}
          <Card className="bg-primary text-white border-0 rounded-3xl p-8 relative overflow-hidden scale-105">
            <Badge className="absolute top-4 right-4 bg-white text-primary">Popular</Badge>
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">Value Pack</h3>
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/20 text-white px-2 py-0.5 rounded-full mb-1" style={{whiteSpace:"nowrap"}}>⚡ Premium text detection — Claude Sonnet 4</span><br/>
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-white/20 text-white px-2 py-0.5 rounded-full mb-2" style={{whiteSpace:"nowrap"}}>🖼️ Detailed AI breakdown of every image scan</span>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold">€9.99</span>
                <span className="text-indigo-200">/month</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8">
              {['200 text scans/month', '100 image scans/month', '20 document scans/month (PPTX, DOCX, PDF)', 'Detects humanized AI text', '🖼️ Detailed AI breakdown of every image scan', 'Best for teachers & professionals'].map(f => (
                <li key={f} className="flex items-start gap-3"><Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" /><span className="text-indigo-100">{f}</span></li>
              ))}
            </ul>
            <Button onClick={() => handlePurchase('value_pack')} disabled={isProcessing} className="bg-white text-primary hover:bg-indigo-50 rounded-full px-8 py-5 font-bold text-lg w-full">
              {isProcessing ? 'Processing...' : 'Get Value Pack'}
            </Button>
          </Card>

          <Card className="bg-white border border-border/50 rounded-3xl p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">Professional</h3>
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full mb-1" style={{whiteSpace:"nowrap"}}>⚡ Premium text detection — Claude Sonnet 4</span><br/>
              <span className="inline-flex items-center gap-1 text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full mb-2" style={{whiteSpace:"nowrap"}}>🖼️ Detailed AI breakdown of every image scan</span>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold">€39.99</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8">
              {['1000 text scans/month', '500 image scans/month', '200 document scans/month (PPTX, DOCX, PDF)', 'Detects humanized AI text', '🖼️ Detailed AI breakdown of every image scan', 'Priority support', 'For businesses'].map(f => (
                <li key={f} className="flex items-start gap-3"><Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" /><span className="text-muted-foreground">{f}</span></li>
              ))}
            </ul>
            <Button onClick={() => handlePurchase('professional')} disabled={isProcessing} className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-5 font-bold text-lg w-full">
              {isProcessing ? 'Processing...' : 'Get Professional'}
            </Button>
          </Card>

          {/* Team - Coming Soon — hidden until ready, restore by uncommenting
          <Card className="bg-white border-2 border-dashed border-indigo-200 rounded-3xl p-8 relative opacity-90">
            <div className="absolute top-4 right-4">
              <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-3 py-1 rounded-full">Coming Soon</span>
            </div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">Team</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-extrabold">€14.99</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </div>
            <ul className="space-y-4 mb-6">
              {['3 team members', '200 text scans/user', '50 image scans/user', '10 video scans/user', 'Perfect for school departments'].map(f => (
                <li key={f} className="flex items-start gap-3"><Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" /><span className="text-muted-foreground">{f}</span></li>
              ))}
            </ul>
            <div className="space-y-3">
              <input
                type="email"
                placeholder="Enter your email to get notified"
                value={teamNotifyEmail}
                onChange={(e) => setTeamNotifyEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-full border border-border text-sm focus:outline-none focus:border-primary"
              />
              <Button
                onClick={handleTeamNotify}
                disabled={teamNotifySubmitted || !teamNotifyEmail}
                className="w-full rounded-full px-8 py-5 font-bold text-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-50">
                {teamNotifySubmitted ? "✓ You'll be notified!" : 'Notify me when available'}
              </Button>
            </div>
          </Card>
          END RESTORE */}
        </div>



        {/* One-time purchases */}
        <div id="top-ups" className="max-w-4xl mx-auto mt-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold tracking-tight mb-2">One-Time Top-Ups</h2>
            <p className="text-muted-foreground">No subscription needed — buy scans once and use them anytime.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white border border-border/50 rounded-3xl p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-1">100 Text Scans</h3>
              <p className="text-muted-foreground text-sm mb-4">Perfect for occasional use — scan essays, articles, assignments.</p>
              <div className="text-4xl font-extrabold mb-6">€4.99</div>
              <Button onClick={() => handleOneTimePurchase('text_100')} disabled={isProcessing} className="bg-blue-600 text-white hover:bg-blue-700 rounded-full px-8 py-3 font-semibold w-full">
                {isProcessing ? 'Processing...' : 'Buy 100 Text Scans'}
              </Button>
            </Card>

            <Card className="bg-white border border-border/50 rounded-3xl p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <ImageIcon className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-1">50 Image Scans</h3>
              <p className="text-muted-foreground text-sm mb-2">Check AI-generated images — photos, artwork, assignments.</p>
              <p className="text-xs text-primary font-semibold mb-4">🖼️ Detailed AI breakdown of every image scan</p>
              <div className="text-4xl font-extrabold mb-6">€4.99</div>
              <Button onClick={() => handleOneTimePurchase('image_50')} disabled={isProcessing} className="bg-purple-600 text-white hover:bg-purple-700 rounded-full px-8 py-3 font-semibold w-full">
                {isProcessing ? 'Processing...' : 'Buy 50 Image Scans'}
              </Button>
            </Card>
          </div>
        </div>

        {/* Custom plan contact */}
        <div className="text-center mt-12 mb-4">
          <p className="text-muted-foreground text-sm">
            Need more? <a href="mailto:support@authenticoapp.com" className="text-primary underline font-medium">Contact us</a> at support@authenticoapp.com for a custom plan.
          </p>
        </div>

        {/* Cancel subscription */}
        {isSubscribed && (
          <div className="mt-12 text-center">
            <button onClick={() => setCancelDialogOpen(true)} className="text-sm text-muted-foreground hover:text-destructive underline transition-colors">
              Cancel subscription
            </button>
          </div>
        )}

        {/* Cancel Dialog */}
        {cancelDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="bg-white rounded-3xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-3">Cancel subscription?</h2>
              <p className="text-muted-foreground mb-6">You'll lose access to your paid scans at the end of your billing period. This can't be undone.</p>
              <div className="flex flex-col gap-3">
                <Button onClick={handleCancelSubscription} className="w-full rounded-full bg-red-600 text-white hover:bg-red-700">Yes, cancel subscription</Button>
                <Button onClick={() => setCancelDialogOpen(false)} variant="outline" className="w-full rounded-full">Keep plan</Button>
              </div>
            </Card>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-sm">✨ Free tier: 1 text/day · Videos on paid plans only</p>
        </div>
      </main>
    </div>
  );
};

// ─── SUCCESS PAGE ─────────────────────────────────────────────────────────────
const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  useEffect(() => {
    if (sessionId) setTimeout(() => navigate('/pricing?session_id=' + sessionId), 2000);
  }, [sessionId, navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50/50 to-white">
      <div className="text-center">
        <div className="w-24 h-24 mx-auto rounded-full bg-success/10 flex items-center justify-center mb-6">
          <Check className="w-12 h-12 text-success" />
        </div>
        <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
        <p className="text-muted-foreground mb-6">Verifying your payment...</p>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    </div>
  );
};


// ─── HISTORY PAGE ─────────────────────────────────────────────────────────────
const HistoryPage = () => {
  const navigate = useNavigate();
  const { open: openAuthModal } = useAuthModal();
  const [detections, setDetections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn()) { openAuthModal(); return; }
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API}/detections/history?limit=20`, axiosConfig());
        setDetections(response.data.detections || []);
      } catch (error) {
        console.error('Failed to fetch history:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const getResultColor = (result) => {
    if (!result) return 'text-gray-500';
    if (result.toLowerCase().includes('ai')) return 'text-orange-600';
    if (result.toLowerCase().includes('human')) return 'text-green-600';
    return 'text-gray-500';
  };

  const getResultBg = (result) => {
    if (!result) return 'bg-gray-50';
    if (result.toLowerCase().includes('ai')) return 'bg-orange-50 border-orange-200';
    if (result.toLowerCase().includes('human')) return 'bg-green-50 border-green-200';
    return 'bg-gray-50 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <Toaster position="top-center" richColors />
      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="authentico" className="w-16 h-16 rounded-2xl" />
            <span className="text-2xl font-bold tracking-tight">authentico</span>
          </Link>
          <Button onClick={() => navigate('/')} className="bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-full px-4 py-2 text-sm font-semibold shadow-sm">← New Scan</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight mb-4">Scan History</h1>
          <p className="text-muted-foreground">Your last 20 scans</p>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <img src="/logo.png" alt="authentico" className="w-12 h-12 mx-auto rounded-2xl animate-pulse" />
            <p className="text-muted-foreground mt-4">Loading history...</p>
          </div>
        ) : detections.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-6">No scans yet — start by scanning something!</p>
            <Button onClick={() => navigate('/')} className="bg-primary text-white rounded-full px-8">
              Start Scanning
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {detections.map((d, i) => (
              <Card key={i} className={`border rounded-2xl p-5 ${getResultBg(d.result)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                      {d.file_type === 'text' ? <FileText className="w-5 h-5 text-primary" /> : <ImageIcon className="w-5 h-5 text-primary" />}
                    </div>
                    <div>
                      <p className="font-semibold text-sm truncate max-w-48">{d.file_name || 'Unknown file'}</p>
                      <p className="text-xs text-muted-foreground">{d.file_type} · {d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold text-sm ${getResultColor(d.result)}`}>{d.result || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{Math.round((d.confidence || 0) * 100)}% AI probability</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};


// ─── PRIVACY POLICY PAGE ──────────────────────────────────────────────────────
const PrivacyPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="authentico" className="w-16 h-16 rounded-2xl" />
            <span className="text-2xl font-bold tracking-tight">authentico</span>
          </Link>
          <Button onClick={() => navigate('/')} className="bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-full px-4 py-2 text-sm font-semibold shadow-sm">← Back to App</Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-extrabold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-10">Last updated: April 2026</p>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">1. Who we are</h2>
            <p>Authentico is an AI content detection service. We help teachers, professionals, and individuals verify whether text or images were generated by artificial intelligence. Authentico is operated as an independent project.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">2. What data we collect</h2>
            <p className="mb-2">We collect the minimum data needed to provide the service:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>A randomly generated anonymous user ID stored in your browser (we do not require account registration)</li>
              <li>Content you submit for scanning (text or images) — this is sent to our AI provider for analysis and not stored permanently</li>
              <li>Scan history (file name, scan type, result, timestamp) linked to your anonymous ID</li>
              <li>Payment information if you purchase a subscription or scan pack — handled entirely by Stripe, we never see your card details</li>
              <li>Basic usage data such as number of scans used</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">3. How we use your data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide the detection service and track your scan allowance</li>
              <li>To process payments and manage your subscription</li>
              <li>To improve detection accuracy over time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">4. Third-party services</h2>
            <p className="mb-2">We use the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Anthropic</strong> — processes your submitted content for AI detection. Content is sent securely and not retained by Anthropic beyond the request.</li>
              <li><strong>Stripe</strong> — handles all payment processing. We never store your payment details.</li>
              <li><strong>MongoDB Atlas</strong> — stores your scan history and account data securely.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">5. Data retention</h2>
            <p>Scan history is retained for up to 90 days. Anonymous user data is retained as long as you use the service. You can request deletion of your data by contacting us.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">6. Cookies</h2>
            <p>We use a single anonymous identifier stored in your browser's local storage to track your scan allowance. We do not use tracking cookies or advertising cookies.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">7. Your rights</h2>
            <p>You have the right to request access to, correction of, or deletion of your data. Since accounts are anonymous by default, data is tied to your browser. Contact us if you need assistance.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">8. Contact</h2>
            <p>For privacy-related questions, contact us at: <a href="mailto:support@authenticoapp.com" className="text-primary underline">support@authenticoapp.com</a></p>
          </section>
        </div>
      </main>
      <footer className="border-t border-border/50 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 flex gap-6 justify-center text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
};

// ─── TERMS OF SERVICE PAGE ────────────────────────────────────────────────────
const TermsPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="authentico" className="w-16 h-16 rounded-2xl" />
            <span className="text-2xl font-bold tracking-tight">authentico</span>
          </Link>
          <Button onClick={() => navigate('/')} className="bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-full px-4 py-2 text-sm font-semibold shadow-sm">← Back to App</Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-extrabold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-10">Last updated: April 2026</p>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">1. Acceptance of terms</h2>
            <p>By using Authentico, you agree to these Terms of Service. If you do not agree, please do not use the service.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">2. Description of service</h2>
            <p>Authentico provides AI-generated content detection for text and images. Results are provided for informational purposes only and should not be used as sole evidence in academic, legal, or professional decisions.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">3. Accuracy disclaimer</h2>
            <p>No AI detection system is 100% accurate. Authentico provides probability-based analysis. We are not liable for decisions made based on our results. Always use your own judgment alongside our analysis.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">4. Acceptable use</h2>
            <p className="mb-2">You agree not to use Authentico to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Upload illegal, harmful, or privacy-violating content</li>
              <li>Attempt to reverse-engineer or abuse the detection system</li>
              <li>Resell or redistribute the service without permission</li>
              <li>Use the service for any unlawful purpose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">5. Payments and subscriptions</h2>
            <p>Subscriptions are billed monthly. One-time scan packs are non-refundable once scans have been used. Unused scans from one-time packs do not expire. You may cancel your subscription at any time — access continues until the end of the billing period.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">6. Free tier</h2>
            <p>Free tier users receive a limited number of scans that replenish daily (text) and weekly (images). Scan allowances are tied to your browser and cannot be transferred.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">7. Limitation of liability</h2>
            <p>Authentico is provided "as is" without warranties of any kind. We are not liable for any damages arising from use of the service, including but not limited to decisions made based on detection results.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">8. Changes to terms</h2>
            <p>We may update these terms from time to time. Continued use of the service after changes constitutes acceptance of the new terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3 text-gray-900">9. Contact</h2>
            <p>For questions about these terms: <a href="mailto:support@authenticoapp.com" className="text-primary underline">support@authenticoapp.com</a></p>
          </section>
        </div>
      </main>
      <footer className="border-t border-border/50 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 flex gap-6 justify-center text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
};

// ─── APP ──────────────────────────────────────────────────────────────────────
// ─── HOW IT WORKS PAGE ────────────────────────────────────────────────────────
const HowItWorksPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <Toaster position="top-center" richColors />
      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="authentico" className="w-12 h-12 rounded-2xl" />
            <span className="text-lg font-bold tracking-tight">authentico</span>
          </Link>
          <Button onClick={() => navigate('/')} className="bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-full px-4 py-2 text-sm font-semibold shadow-sm">← New Scan</Button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1.5 h-6 rounded-full bg-primary" />
          <h1 className="text-2xl font-extrabold tracking-tight">How It Works</h1>
        </div>
        <div className="space-y-4">
          {[
            { step: '1', emoji: '📋', title: 'Choose your content type', sub: 'Pick a scan mode', body: 'Pick from Image, Paste Text, Document, or Take Photo. Each mode is optimized for that content type — text uses linguistic analysis, images use visual pattern detection.' },
            { step: '2', emoji: '🚀', title: 'Upload or paste your content', sub: 'Add your file or text', body: 'Drop in a file, paste an essay, or point your camera. Authentico supports PNG, JPG, WEBP images, plus PPTX, DOCX, and PDF documents on paid plans.' },
            { step: '3', emoji: '🔍', title: 'AI analyzes the content', sub: 'Detection runs instantly', body: 'Our engine — powered by Claude Sonnet for paid users and Sightengine for images — scans for AI patterns, linguistic markers, and statistical signatures in seconds.' },
            { step: '4', emoji: '✅', title: 'Get a clear verdict', sub: 'See your result', body: 'You receive a percentage score, a Likely AI / Likely Human verdict, and a detailed breakdown of the specific indicators found — so you always understand the why.' },
          ].map(({ step, emoji, title, sub, body }) => (
            <div key={step} className="flex items-start gap-3 p-4 bg-white border border-indigo-100 rounded-2xl shadow-sm">
              <span className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-lg flex-shrink-0 shadow-sm">{emoji}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-bold text-primary bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5">Step {step}</span>
                </div>
                <p className="text-sm font-bold text-gray-800">{title}</p>
                <p className="text-xs text-gray-400 mt-0.5 mb-2">{sub}</p>
                <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 p-4 bg-primary rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <p className="text-sm font-bold text-white">Ready to try it?</p>
            <p className="text-xs text-white/70 mt-0.5">Run your first scan for free</p>
          </div>
          <Button onClick={() => navigate('/')} className="bg-white text-primary font-bold rounded-full px-4 py-2 text-sm shadow-sm">Scan now →</Button>
        </div>
      </main>
    </div>
  );
};

// ─── FAQ PAGE ─────────────────────────────────────────────────────────────────
const FAQPage = () => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(null);
  const faqs = [
    { q: 'What is AI content detection?', a: 'AI content detection analyzes text, images, or documents to determine whether they were created by a human or generated by a model like ChatGPT, GPT-4, or Midjourney. Authentico detects patterns, structure, and linguistic markers that indicate AI authorship.' },
    { q: 'How accurate is Authentico?', a: 'Authentico uses Claude — one of the most advanced AI models available — for text analysis, and Sightengine for images. No detector is 100% accurate, but we provide a detailed confidence breakdown so you always understand the reasoning. Use it as one tool among several for important decisions.' },
    { q: 'Can teachers use it to catch AI-written essays?', a: 'Yes — Authentico is built with educators in mind. Paste essay text directly, or upload student submissions as Word documents, PDFs, or PowerPoint presentations. You get a clear verdict plus an explanation of the patterns detected.' },
    { q: 'What file types are supported?', a: 'Text scanning via paste, image scanning for PNG, JPG, JPEG, WEBP, and document scanning for PPTX, DOCX, PDF (paid plans). You can also use your device camera to capture and scan content directly.' },
    { q: 'Is Authentico free?', a: 'Yes — the free tier includes 1 text scan per day and 2 image scans per week. Paid plans start at €5.99/month and add document scanning, higher limits, and Claude Sonnet for premium text analysis. One-time scan packs are also available.' },
    { q: 'Is my content kept private?', a: "Authentico does not store the content you submit. Scans are processed securely and used only to generate your result. No account registration required — you're identified by an anonymous browser ID only." },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50/50 to-white">
      <Toaster position="top-center" richColors />
      <header className="border-b border-border/50 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="authentico" className="w-12 h-12 rounded-2xl" />
            <span className="text-lg font-bold tracking-tight">authentico</span>
          </Link>
          <Button onClick={() => navigate('/')} className="bg-white border border-border text-gray-700 hover:bg-gray-50 rounded-full px-4 py-2 text-sm font-semibold shadow-sm">← New Scan</Button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1.5 h-6 rounded-full bg-primary" />
          <h1 className="text-2xl font-extrabold tracking-tight">FAQ</h1>
        </div>
        <div className="space-y-3">
          {faqs.map(({ q, a }, i) => (
            <div key={i} className="bg-white border border-indigo-100 rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center gap-3 px-4 py-4 text-left active:bg-indigo-50 transition-colors">
                <span className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center text-sm flex-shrink-0 shadow-sm">❓</span>
                <p className="text-sm font-bold text-gray-800 flex-1">{q}</p>
                <span className={`text-indigo-300 font-bold text-lg transition-transform ${open === i ? 'rotate-90' : ''}`}>›</span>
              </button>
              {open === i && (
                <div className="px-4 pb-4 pt-0">
                  <div className="h-px bg-indigo-100 mb-3" />
                  <p className="text-xs text-gray-500 leading-relaxed pl-11">{a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-8 p-4 bg-primary rounded-2xl flex items-center justify-between shadow-sm">
          <div>
            <p className="text-sm font-bold text-white">Still have questions?</p>
            <p className="text-xs text-white/70 mt-0.5">Contact support@authenticoapp.com</p>
          </div>
          <Button onClick={() => navigate('/')} className="bg-white text-primary font-bold rounded-full px-4 py-2 text-sm shadow-sm">Scan now →</Button>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthModalProvider>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
            </Routes>
          </AuthModalProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
